import React, { createContext, useContext, useState, useEffect, useCallback, Suspense, lazy, useRef } from "react";
import "./App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { OfflineProvider, useOffline } from "./context/OfflineContext";
import { SecurityProvider, useSecurity, createSession, clearSession, addAuditLog, is2FAEnabled, is2FAVerified, checkRateLimit, clearRateLimit } from "./context/SecurityContext";
import { authAPI } from "./services/api";
import locationTracker from "./services/backgroundLocation";
import { subscribeToPush, isPushSupported } from "./utils/notifications";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SidebarNavigation from "./components/SidebarNavigation";
import NotificationBell from "./components/NotificationBell";
import GeofenceMonitor from "./components/GeofenceMonitor";
import { 
  Clock, Home, Building2, Users, Calendar, FileText, 
  BarChart3, Settings, LogOut, Menu, X, Bell, 
  Megaphone, Sun, Moon, CalendarDays, Receipt, 
  Clock3, ArrowLeftRight, WifiOff, RefreshCw,
  Fingerprint, ChevronLeft, AlertTriangle, MapPin
} from "lucide-react";

// Lazy load pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const StaffDashboard = lazy(() => import("./pages/StaffDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const BranchManagement = lazy(() => import("./pages/BranchManagement"));
const StaffManagement = lazy(() => import("./pages/StaffManagement"));
const LeaveApplication = lazy(() => import("./pages/LeaveApplication"));
const LeaveManagement = lazy(() => import("./pages/LeaveManagement"));
const AttendanceReports = lazy(() => import("./pages/AttendanceReports"));
const DailySchedule = lazy(() => import("./pages/DailySchedule"));
const FaceRegistration = lazy(() => import("./pages/FaceRegistration"));
const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
const OvertimeManagement = lazy(() => import("./pages/OvertimeManagement"));
const OvertimePage = lazy(() => import("./pages/OvertimePage"));
const ShiftSwap = lazy(() => import("./pages/ShiftSwap"));
const PublicHolidays = lazy(() => import("./pages/PublicHolidays"));
const Announcements = lazy(() => import("./pages/Announcements"));
const PayslipGenerator = lazy(() => import("./pages/PayslipGenerator"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const LeaveCalendarPage = lazy(() => import("./pages/LeaveCalendarPage"));
const TeamViewPage = lazy(() => import("./pages/TeamViewPage"));
const LocationTrackingPage = lazy(() => import("./pages/LocationTrackingPage"));
const WhatsAppDashboard = lazy(() => import("./pages/WhatsAppDashboard"));
const CarryHourReport = lazy(() => import("./pages/CarryHourReport"));
const CarryOTTracker = lazy(() => import("./pages/CarryOTTracker"));
const ThemeDemo = lazy(() => import("./pages/ThemeDemo"));
const CrossCheckPage = lazy(() => import("./pages/CrossCheckPage"));
const LeaveBalanceHistory = lazy(() => import("./pages/LeaveBalanceHistory"));
const BiodataPage = lazy(() => import("./pages/BiodataPage"));
const FaceRecordsPage = lazy(() => import("./pages/FaceRecordsPage"));
const WorkDaysSettingsPage = lazy(() => import("./pages/WorkDaysSettingsPage"));
const BackupRestorePage = lazy(() => import("./pages/BackupRestorePage"));
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));
const WeeklySummary = lazy(() => import("./pages/WeeklySummary"));

// Auth context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("destiny_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  // Set correct page title
  useEffect(() => {
    document.title = "Rekod & Pengurusan Staff - My Destiny Junior";
  }, []);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("session_token");
      if (token) {
        try {
          const userData = await authAPI.me();
          setUser(userData);
          localStorage.setItem("destiny_user", JSON.stringify(userData));
        } catch (e) {
          // Token invalid, clear
          localStorage.removeItem("session_token");
          localStorage.removeItem("destiny_user");
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = useCallback(async (email, password, skipRateLimit = false) => {
    // Rate limiting check
    if (!skipRateLimit) {
      const rateCheck = checkRateLimit(`login_${email}`, 5, 60000);
      if (!rateCheck.allowed) {
        throw new Error(rateCheck.message);
      }
    }

    try {
      const data = await authAPI.login(email, password);
      const userData = data.user;
      setUser(userData);
      localStorage.setItem("destiny_user", JSON.stringify(userData));
      createSession(userData.user_id);
      clearRateLimit(`login_${email}`);
      // Auto-subscribe to push notifications after login
      if (isPushSupported()) {
        subscribeToPush().catch(() => {});
      }
      return userData;
    } catch (error) {
      throw error;
    }
  }, []);

  const register = useCallback(async (email, password, name, role) => {
    try {
      const data = await authAPI.register(email, password, name, role);
      // Registration now returns pending status, no auto-login
      return data;
    } catch (error) {
      throw new Error(error.message || "Pendaftaran gagal");
    }
  }, []);

  const logout = useCallback(async () => {
    locationTracker.stop();
    await authAPI.logout();
    setUser(null);
    clearSession();
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem("destiny_user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Auto-start location tracking for staff
  useEffect(() => {
    if (user && !loading) {
      // Start background location tracking
      locationTracker.start().then((started) => {
        if (started) {
          console.log('[App] Location tracking started for', user.name);
        }
      });
      // Auto-subscribe to push if not already
      if (isPushSupported() && !localStorage.getItem('destiny_push_subscribed')) {
        subscribeToPush().catch(() => {});
      }
    }
    return () => {
      // Don't stop on unmount - let it keep tracking
    };
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center hero-pattern dark:bg-stone-950">
        <div className="flex flex-col items-center gap-4 animate-bounce-in">
          <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center animate-pulse-glow">
            <Clock className="w-8 h-8 text-white" />
          </div>
          <p className="text-stone-500 dark:text-stone-400 font-medium">Mengesahkan...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Loading spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center hero-pattern dark:bg-stone-950">
    <div className="flex flex-col items-center gap-4 animate-bounce-in">
      <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center animate-pulse-glow">
        <Clock className="w-8 h-8 text-white" />
      </div>
      <p className="text-stone-500 dark:text-stone-400 font-medium">Memuatkan...</p>
    </div>
  </div>
);

// Offline banner
const OfflineBanner = () => {
  const { isOnline, pendingCount, isSyncing, syncOfflineData } = useOffline();

  if (isOnline && pendingCount === 0) return null;

  return (
    <div className={`offline-banner flex items-center justify-center gap-2 ${isOnline ? 'bg-emerald-500' : ''}`}>
      {isOnline ? (
        <>
          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          <span>{pendingCount} data menunggu disegerakkan</span>
          {!isSyncing && (
            <button onClick={syncOfflineData} className="underline ml-2">Segerak sekarang</button>
          )}
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4" />
          <span>Anda sedang offline. Data akan disegerakkan bila online.</span>
        </>
      )}
    </div>
  );
};

// Session Warning Modal
const SessionWarningModal = () => {
  const { sessionWarning, remainingTime, extendSession, showTimeoutModal, setShowTimeoutModal } = useSecurity();
  const navigate = useNavigate();

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (showTimeoutModal) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md glass dark:bg-stone-800/95 border-orange-100 dark:border-stone-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-800 dark:text-stone-100">
              <Clock className="w-5 h-5 text-rose-500" />
              Sesi Telah Tamat
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-stone-600 dark:text-stone-400">
              Sesi anda telah tamat kerana tidak aktif. Sila log masuk semula.
            </p>
            <Button onClick={() => { setShowTimeoutModal(false); navigate('/login'); }} className="w-full btn-primary rounded-xl text-white">
              Log Masuk Semula
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!sessionWarning) return null;

  return (
    <Dialog open={sessionWarning} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md glass dark:bg-stone-800/95 border-orange-100 dark:border-stone-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-stone-800 dark:text-stone-100">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Sesi Hampir Tamat
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-center">
            <p className="text-amber-700 dark:text-amber-300 mb-2">Sesi akan tamat dalam:</p>
            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{formatTime(remainingTime)}</p>
          </div>
          <p className="text-stone-600 dark:text-stone-400 text-center">
            Klik butang di bawah untuk meneruskan sesi anda.
          </p>
          <Button onClick={extendSession} className="w-full btn-primary rounded-xl text-white">
            Teruskan Sesi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Desktop Navigation with Sidebar Toggle
const DesktopNav = ({ navItems, isAdmin, onMenuClick }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 glass dark:bg-stone-900/80 border-b border-orange-100 dark:border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onMenuClick} className="rounded-xl hover:bg-orange-50 dark:hover:bg-stone-800">
              <Menu className="w-5 h-5" />
            </Button>
            <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg overflow-hidden shadow-md flex-shrink-0">
                <img src="/logo.jpeg" alt="My Destiny Junior" className="w-full h-full object-cover" loading="eager" onError={(e) => { e.target.onerror = null; e.target.src = '/logo192.png'; }} />
              </div>
              <span className="font-bold text-base hidden sm:block">
                <span className="gradient-text">My Destiny</span>
                <span className="text-stone-800 dark:text-stone-100"> Junior</span>
              </span>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link flex items-center gap-2 ${location.pathname === item.path ? 'active' : ''}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <NotificationBell />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl hover:bg-orange-100 dark:hover:bg-stone-800 transition-colors"
            >
              {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-indigo-500" />}
            </button>
            
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-orange-50 dark:bg-stone-800">
              <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center text-white text-xs font-bold">
                {user?.name?.charAt(0)}
              </div>
              <span className="text-sm font-medium text-stone-700 dark:text-stone-300">{user?.name?.split(' ')[0]}</span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => { logout(); navigate('/login'); }}
              className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 rounded-xl"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

// Mobile Bottom Navigation
const MobileNav = ({ navItems, onMenuClick }) => {
  const location = useLocation();
  const displayItems = navItems.slice(0, 4);

  return (
    <nav className="mobile-nav lg:hidden">
      <div className="flex items-center justify-around py-2">
        {displayItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all ${
                isActive 
                  ? 'text-orange-600 dark:text-orange-400' 
                  : 'text-stone-500 dark:text-stone-400'
              }`}
            >
              <div className={`p-2 rounded-xl transition-all ${isActive ? 'bg-orange-100 dark:bg-orange-900/30' : ''}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          );
        })}
        <button
          onClick={onMenuClick}
          className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-stone-500 dark:text-stone-400"
        >
          <div className="p-2 rounded-xl">
            <Menu className="w-5 h-5" />
          </div>
          <span className="text-xs font-medium">Menu</span>
        </button>
      </div>
    </nav>
  );
};

// Impersonation Banner
const ImpersonationBanner = () => {
  const isImpersonating = localStorage.getItem("impersonating") === "true";
  const impersonatedBy = localStorage.getItem("impersonated_by") || "Admin";
  const { user } = useAuth();

  const stopImpersonating = () => {
    const adminToken = localStorage.getItem("admin_session_token");
    const adminUser = localStorage.getItem("admin_user");
    if (adminToken) {
      localStorage.setItem("session_token", adminToken);
      localStorage.setItem("destiny_user", adminUser);
      localStorage.removeItem("admin_session_token");
      localStorage.removeItem("admin_user");
      localStorage.removeItem("impersonating");
      localStorage.removeItem("impersonated_by");
      window.location.href = "/admin";
    }
  };

  if (!isImpersonating) return null;

  return (
    <div className="bg-amber-500 text-white text-center py-2 px-4 flex items-center justify-center gap-3 text-sm font-medium z-50 relative" data-testid="impersonation-banner">
      <AlertTriangle className="w-4 h-4" />
      <span>Anda sedang menyamar sebagai <strong>{user?.name}</strong> (oleh {impersonatedBy})</span>
      <button onClick={stopImpersonating} className="ml-2 px-3 py-1 bg-white text-amber-600 rounded-lg text-xs font-bold hover:bg-amber-50 transition-colors" data-testid="stop-impersonate-btn">
        Berhenti Menyamar
      </button>
    </div>
  );
};

// Main Layout
const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "supervisor" || user?.role === "branch_manager";

  const adminNavItems = [
    { path: "/admin", label: "Utama", icon: Home },
    { path: "/staff", label: "Staf", icon: Users },
    { path: "/reports", label: "Laporan", icon: FileText },
    { path: "/analytics", label: "Analitik", icon: BarChart3 },
  ];

  const branchManagerNavItems = [
    { path: "/admin", label: "Utama", icon: Home },
    { path: "/staff", label: "Staf", icon: Users },
    { path: "/reports", label: "Laporan", icon: FileText },
    { path: "/analytics", label: "Analitik", icon: BarChart3 },
  ];

  const supervisorNavItems = [
    { path: "/dashboard", label: "Clock In", icon: Clock3 },
    { path: "/admin", label: "Utama", icon: Home },
    { path: "/staff", label: "Staf", icon: Users },
    { path: "/reports", label: "Laporan", icon: FileText },
  ];

  const staffNavItems = [
    { path: "/dashboard", label: "Utama", icon: Home },
    { path: "/leave", label: "Cuti", icon: Calendar },
    { path: "/overtime", label: "OT", icon: Clock3 },
    { path: "/shift-swap", label: "Tukar", icon: ArrowLeftRight },
  ];

  const navItems = user?.role === "supervisor" ? supervisorNavItems : (user?.role === "branch_manager" ? branchManagerNavItems : (isAdmin ? adminNavItems : staffNavItems));

  return (
    <SecurityProvider user={user} onLogout={logout}>
      <div className="min-h-screen hero-pattern dark:bg-stone-950">
        <ImpersonationBanner />
        <OfflineBanner />
        <SessionWarningModal />
        <SidebarNavigation isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} />
        <DesktopNav navItems={navItems} isAdmin={isAdmin} onMenuClick={() => setSidebarOpen(true)} />
        <main className="pb-24 lg:pb-8">
          {children}
        </main>
        <MobileNav navItems={navItems} onMenuClick={() => setSidebarOpen(true)} />
      </div>
    </SecurityProvider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole, skipFaceCheck }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return <Navigate to="/login" replace />;

  // Allow access to all routes when impersonating
  const isImpersonating = localStorage.getItem("impersonating") === "true";

  if (requiredRole && !isImpersonating) {
    const hasAccess = Array.isArray(requiredRole) 
      ? requiredRole.includes(user.role)
      : user.role === requiredRole;
    
    if (!hasAccess) {
      return <Navigate to={["staff", "supervisor"].includes(user.role) ? "/dashboard" : "/admin"} replace />;
    }
  }

  // Force face registration for staff/supervisor (skip when impersonating)
  if ((user.role === "staff" || user.role === "supervisor") && !user.face_registered && !skipFaceCheck && !isImpersonating && location.pathname !== "/face-register") {
    return <Navigate to="/face-register" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

// Face Register Wrapper (full-screen, no layout)
const FaceRegisterWrapper = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "branch_manager") return <Navigate to="/admin" replace />;

  const handleComplete = () => {
    updateUser({ face_registered: true });
    navigate("/dashboard", { replace: true });
  };

  return <FaceRegistration user={user} onComplete={handleComplete} />;
};

// App Routes
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to={["staff", "supervisor"].includes(user.role) ? "/dashboard" : "/admin"} replace /> : <LoginPage />} />
        
        {/* Staff Routes */}
        <Route path="/dashboard" element={<ProtectedRoute requiredRole={["staff", "supervisor"]}><StaffDashboard /></ProtectedRoute>} />
        <Route path="/leave" element={<ProtectedRoute requiredRole={["staff", "supervisor"]}><LeaveApplication /></ProtectedRoute>} />
        <Route path="/face-register" element={<FaceRegisterWrapper />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute requiredRole={["admin", "supervisor", "branch_manager"]}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/branches" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><BranchManagement /></ProtectedRoute>} />
        <Route path="/staff" element={<ProtectedRoute requiredRole={["admin", "supervisor", "branch_manager"]}><StaffManagement /></ProtectedRoute>} />
        <Route path="/leave-manage" element={<ProtectedRoute requiredRole={["admin", "supervisor", "branch_manager"]}><LeaveManagement /></ProtectedRoute>} />
        <Route path="/payslip" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><PayslipGenerator /></ProtectedRoute>} />
        <Route path="/audit-log" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><AuditLogPage /></ProtectedRoute>} />
        <Route path="/location-tracking" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><LocationTrackingPage /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><WhatsAppDashboard /></ProtectedRoute>} />
        
        {/* Shared Routes */}
        <Route path="/schedule" element={<ProtectedRoute><DailySchedule /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><AttendanceReports /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><AnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/overtime" element={<ProtectedRoute><OvertimeManagement /></ProtectedRoute>} />
        <Route path="/overtime-approval" element={<ProtectedRoute><OvertimePage /></ProtectedRoute>} />
        <Route path="/shift-swap" element={<ProtectedRoute><ShiftSwap /></ProtectedRoute>} />
        <Route path="/holidays" element={<ProtectedRoute><PublicHolidays /></ProtectedRoute>} />
        <Route path="/announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
        <Route path="/leave-calendar" element={<ProtectedRoute><LeaveCalendarPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><TeamViewPage /></ProtectedRoute>} />
        <Route path="/carry-hour" element={<ProtectedRoute><CarryHourReport /></ProtectedRoute>} />
        <Route path="/carry-ot" element={<ProtectedRoute><CarryOTTracker /></ProtectedRoute>} />
        <Route path="/theme-demo" element={<ProtectedRoute><ThemeDemo /></ProtectedRoute>} />
        <Route path="/crosscheck" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><CrossCheckPage /></ProtectedRoute>} />
        <Route path="/leave-history" element={<ProtectedRoute requiredRole={["admin", "branch_manager"]}><LeaveBalanceHistory /></ProtectedRoute>} />
        <Route path="/biodata" element={<ProtectedRoute><BiodataPage /></ProtectedRoute>} />
        <Route path="/face-records" element={<ProtectedRoute requiredRole={["admin", "supervisor"]}><FaceRecordsPage /></ProtectedRoute>} />
        <Route path="/work-days" element={<ProtectedRoute requiredRole={["admin", "supervisor", "branch_manager"]}><WorkDaysSettingsPage /></ProtectedRoute>} />
        <Route path="/backup" element={<ProtectedRoute requiredRole={["admin"]}><BackupRestorePage /></ProtectedRoute>} />
        <Route path="/system-status" element={<ProtectedRoute requiredRole={["admin"]}><SystemStatusPage /></ProtectedRoute>} />
        <Route path="/weekly-summary" element={<ProtectedRoute><WeeklySummary /></ProtectedRoute>} />
        
        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <GeofenceMonitor />
    </Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <OfflineProvider>
        <AuthProvider>
          <div className="App">
            <Toaster 
              position="top-center" 
              richColors 
              toastOptions={{
                className: 'rounded-2xl',
                style: {
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(249, 115, 22, 0.2)',
                }
              }}
            />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </div>
        </AuthProvider>
      </OfflineProvider>
    </ThemeProvider>
  );
}

export default App;
